import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, MapPin, Check, ArrowRight, Calendar, MessageCircle } from 'lucide-react';
import '../styles/Services.css';

const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const activities = [
    {
      id: 'canyoning',
      category: 'Canyoning Adventures',
      description: 'Experience the thrill of descending through Madeira\'s stunning gorges and waterfalls',
      image: 'https://images.unsplash.com/photo-1591696331096-54772dc22f1d?w=1200&h=800&fit=crop',
      tours: [
        {
          id: 'canyoning-beginner',
          title: 'Canyoning Beginner',
          price: '70€',
          duration: '2-3 hours',
          difficulty: 'Beginner',
          minAge: '8+',
          groupSize: 'Small groups',
          description: 'Perfect first contact with canyoning through routes like Ribeira das Cales, Rochão or Ribeiro Frio. No previous experience needed.',
          includes: [
            'Certified equipment',
            'Professional guide',
            'Photos & videos included',
            'Pick-up and drop-off',
            'Insurance'
          ],
          highlights: [
            'Family-friendly activity',
            'Beautiful natural pools',
            'Small waterfalls',
            'Safe environment for beginners'
          ]
        },
        {
          id: 'canyoning-adventure',
          title: 'Canyoning Adventure',
          price: '80€',
          duration: '2-3 hours',
          difficulty: 'Intermediate',
          minAge: '8+',
          groupSize: 'Small groups',
          description: 'Level up your adventure with routes at Ribeira do Cidrão, Ribeira do Lajeado, Ribeira do Alecrim or Ribeiro Frio.',
          includes: [
            'Certified equipment',
            'Professional guide',
            'Photos & videos included',
            'Pick-up and drop-off',
            'Insurance'
          ],
          highlights: [
            'Multiple waterfall descents',
            'Natural water slides',
            'Stunning gorge scenery',
            'Thrilling rappels'
          ]
        },
        {
          id: 'canyoning-advanced',
          title: 'Canyoning Advanced',
          price: '150€',
          duration: 'Up to 5 hours',
          difficulty: 'Advanced',
          minAge: '16+',
          groupSize: 'Small groups',
          description: 'For experienced adventurers. Routes include Ribeira Funda, Ribeira da Furna and Ribeira da Camisa. Previous canyoning experience required.',
          includes: [
            'Professional equipment',
            'Expert guide',
            'Photos & videos included',
            'Pick-up and drop-off',
            'Insurance'
          ],
          highlights: [
            'Technical descents',
            'Remote locations',
            'Challenging rappels',
            'Spectacular wild nature'
          ]
        }
      ]
    },
    {
      id: 'jeep-tours',
      category: 'Jeep Tours Private',
      description: 'Explore Madeira\'s rugged terrain and hidden treasures in style and comfort',
      image: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200&h=800&fit=crop',
      tours: [
        {
          id: 'jeep-half-day',
          title: 'Private Jeep Tour - Half Day',
          price: '190€',
          priceNote: 'per vehicle (up to 8 people)',
          duration: '4 hours',
          difficulty: 'Easy',
          minAge: 'All ages',
          groupSize: 'Private (1-8 people)',
          description: 'Discover Madeira\'s hidden viewpoints and off-road trails with your own private driver-guide.',
          includes: [
            'Private 4x4 vehicle',
            'Professional driver-guide',
            'Pick-up and drop-off',
            'Flexible itinerary',
            'Bottled water'
          ],
          highlights: [
            'Stunning mountain viewpoints',
            'Traditional villages',
            'Off-road adventure',
            'Customizable route'
          ]
        },
        {
          id: 'jeep-full-day',
          title: 'Private Jeep Tour - Full Day',
          price: '310€',
          priceNote: 'per vehicle (up to 8 people)',
          duration: '8 hours',
          difficulty: 'Easy',
          minAge: 'All ages',
          groupSize: 'Private (1-8 people)',
          description: 'Full day exploration of Madeira\'s most spectacular landscapes and secret locations accessible only by 4x4.',
          includes: [
            'Private 4x4 vehicle',
            'Professional driver-guide',
            'Pick-up and drop-off',
            'Flexible itinerary',
            'Lunch stop included',
            'Bottled water'
          ],
          highlights: [
            'Comprehensive island tour',
            'Remote locations',
            'Authentic local experience',
            'Maximum flexibility'
          ]
        }
      ]
    },
    {
      id: 'levada-walks',
      category: 'Levada Walks & Hiking',
      description: 'Walk along Madeira\'s famous irrigation channels through lush forests and breathtaking landscapes',
      image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=1200&h=800&fit=crop',
      tours: [
        {
          id: 'levada-walk',
          title: 'Levada Walk with Guide',
          price: '40€',
          priceNote: 'per person',
          duration: '4-6 hours',
          difficulty: 'Moderate',
          minAge: '8+',
          groupSize: 'Small groups',
          description: 'Explore Madeira\'s UNESCO World Heritage Laurisilva forest along historic levada channels with an expert guide.',
          includes: [
            'Professional guide',
            'Pick-up and drop-off',
            'Walking poles (if needed)',
            'Insurance'
          ],
          highlights: [
            '25 Fontes waterfall',
            'Laurisilva forest',
            'Stunning valley views',
            'Local flora & fauna'
          ],
          routes: [
            'Levada das 25 Fontes',
            'Levada do Caldeirão Verde',
            'Levada do Rei',
            'Vereda da Ponta de São Lourenço'
          ]
        }
      ]
    }
  ];

  const openBookingModal = (activity, tour) => {
    setSelectedActivity({ ...tour, category: activity.category });
  };

  const closeModal = () => {
    setSelectedActivity(null);
  };

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="services-hero-overlay"></div>
        <div className="container">
          <div className={`services-hero-content ${isVisible ? 'animated fadeIn' : 'animated'}`}>
            <h1 className="display-medium">Daily Tours & Adventures</h1>
            <p className="body-large">
              Book your perfect Madeira adventure today - from thrilling canyoning to scenic jeep tours and peaceful levada walks
            </p>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section className="activities-section">
        <div className="container">
          {activities.map((activity, index) => (
            <div key={activity.id} className="activity-category" id={activity.id}>
              {/* Category Header */}
              <div className="category-header">
                <div className="category-info">
                  <h2 className="heading-2">{activity.category}</h2>
                  <p className="body-large">{activity.description}</p>
                </div>
                <div className="category-image">
                  <img src={activity.image} alt={activity.category} />
                </div>
              </div>

              {/* Tours Grid - Exploor Peru Style */}
              <div className="tours-grid">
                {activity.tours.map((tour) => (
                  <div key={tour.id} className="tour-card-exploor">
                    {/* Tour Image */}
                    <div 
                      className="tour-card-image-exploor"
                      style={{ backgroundImage: `url(${activity.image})` }}
                    >
                      <img src={activity.image} alt={tour.title} style={{ opacity: 0 }} />
                    </div>

                    <div className="tour-card-content-exploor">
                      <h3 className="tour-title-exploor">{tour.title}</h3>
                      <p className="tour-subtitle-exploor">{tour.description}</p>
                      
                      {/* Highlights as destinations */}
                      <div className="tour-destinations-exploor">
                        <MapPin size={14} />
                        <span>{tour.highlights.slice(0, 2).join(', ')}</span>
                      </div>

                      <div className="tour-meta-exploor">
                        <div className="tour-duration-exploor">
                          <Clock size={14} />
                          <span>{tour.duration}</span>
                        </div>
                        <div className="tour-price-exploor">
                          <span className="from">from: </span>{tour.price}
                        </div>
                      </div>

                      <button 
                        className="tour-book-button-exploor"
                        onClick={() => openBookingModal(activity, tour)}
                      >
                        See more
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <div className="container">
          <div className="services-cta-content">
            <h2 className="heading-2">Ready for Your Madeira Adventure?</h2>
            <p className="body-large">
              Contact our team to book your tour or get a customized experience
            </p>
            <div className="services-cta-buttons">
              <a href="https://wa.me/351968656613" target="_blank" rel="noopener noreferrer" className="btn-primary">
                <MessageCircle size={20} />
                WhatsApp Us
              </a>
              <Link to="/contact" className="btn-secondary">
                Contact Form
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      {selectedActivity && (
        <div className="booking-modal-overlay" onClick={closeModal}>
          <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <h3 className="modal-title">{selectedActivity.title}</h3>
            <p className="modal-category">{selectedActivity.category}</p>
            <div className="modal-price">{selectedActivity.price}</div>
            
            <div className="modal-contact-info">
              <p><strong>To book this tour, please contact us:</strong></p>
              <a href="https://wa.me/351968656613" target="_blank" rel="noopener noreferrer" className="modal-contact-link">
                📞 +351 968 656 613
              </a>
              <a href="mailto:bookings@madeiraoutdoors.com" className="modal-contact-link">
                ✉️ bookings@madeiraoutdoors.com
              </a>
              <a href="https://wa.me/351968656613" target="_blank" rel="noopener noreferrer" className="modal-contact-link whatsapp">
                💬 WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
